import React, { useRef, useEffect, useCallback } from "react";

export const AnnotationOverlay = ({ annotations, width, height }) => {
    const canvasRef = useRef(null);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        let data;
        try {
            data = typeof annotations === "string" ? JSON.parse(annotations) : annotations;
        } catch {
            return;
        }
        if (!data || !data.shapes || data.shapes.length === 0) return;

        const ctx = canvas.getContext("2d");
        const origW = data.width || 600;
        const origH = data.height || 600;
        const scaleX = width / origW;
        const scaleY = height / origH;

        canvas.width = width;
        canvas.height = height;
        ctx.clearRect(0, 0, width, height);
        ctx.save();
        ctx.scale(scaleX, scaleY);

        data.shapes.forEach((shape) => {
            ctx.save();
            ctx.strokeStyle = shape.color || "#ef4444";
            ctx.lineWidth = shape.strokeWidth || 3;
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            ctx.fillStyle = "transparent";

            switch (shape.type) {
                case "freehand":
                    if (shape.points && shape.points.length >= 2) {
                        ctx.beginPath();
                        ctx.moveTo(shape.points[0][0], shape.points[0][1]);
                        for (let i = 1; i < shape.points.length; i++) {
                            ctx.lineTo(shape.points[i][0], shape.points[i][1]);
                        }
                        ctx.stroke();
                    }
                    break;
                case "line":
                    ctx.beginPath();
                    ctx.moveTo(shape.x1, shape.y1);
                    ctx.lineTo(shape.x2, shape.y2);
                    ctx.stroke();
                    break;
                case "arrow": {
                    const dx = shape.x2 - shape.x1;
                    const dy = shape.y2 - shape.y1;
                    const angle = Math.atan2(dy, dx);
                    const headLen = Math.max(15, Math.sqrt(dx * dx + dy * dy) * 0.2);
                    ctx.beginPath();
                    ctx.moveTo(shape.x1, shape.y1);
                    ctx.lineTo(shape.x2, shape.y2);
                    ctx.stroke();
                    ctx.beginPath();
                    ctx.moveTo(shape.x2, shape.y2);
                    ctx.lineTo(shape.x2 - headLen * Math.cos(angle - 0.4), shape.y2 - headLen * Math.sin(angle - 0.4));
                    ctx.moveTo(shape.x2, shape.y2);
                    ctx.lineTo(shape.x2 - headLen * Math.cos(angle + 0.4), shape.y2 - headLen * Math.sin(angle + 0.4));
                    ctx.stroke();
                    break;
                }
                case "circle":
                    if (shape.radius > 0) {
                        ctx.beginPath();
                        ctx.arc(shape.cx, shape.cy, shape.radius, 0, Math.PI * 2);
                        ctx.stroke();
                    }
                    break;
                case "ellipse":
                    if (shape.rx > 0 || shape.ry > 0) {
                        ctx.beginPath();
                        ctx.ellipse(shape.cx, shape.cy, Math.abs(shape.rx), Math.abs(shape.ry), 0, 0, Math.PI * 2);
                        ctx.stroke();
                    }
                    break;
                case "rectangle":
                    ctx.beginPath();
                    ctx.rect(shape.x, shape.y, shape.w, shape.h);
                    ctx.stroke();
                    break;
                case "text":
                    ctx.fillStyle = shape.color || "#ef4444";
                    ctx.font = `${shape.fontSize || 16}px Helvetica, Arial, sans-serif`;
                    ctx.fillText(shape.text || "", shape.x, shape.y);
                    break;
                default:
                    break;
            }
            ctx.restore();
        });

        ctx.restore();
    }, [annotations, width, height]);

    useEffect(() => {
        draw();
    }, [draw]);

    if (!annotations) return null;

    return (
        <canvas
            ref={canvasRef}
            width={width}
            height={height}
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                width,
                height,
                pointerEvents: "none",
            }}
        />
    );
};
